import type { ChiroTool, ChiroToolHandler } from '../types';
import { distanceHandler, angle3PointHandler, angle4PointHandler } from './generic';
import {
  referenceLineHandler,
  lineWithPerpDistanceHandler,
  angleFromHorizontalHandler,
  distancesFromCenterHandler,
} from './specialised';
import {
  twoLinesIntersectionHandler,
  ratioHandler,
  perPairDistanceHandler,
  perLevelAngleHandler,
  polylineHandler,
} from './multi';
import toolsData from '../data/tools.json';

/**
 * Bucket registry — tools listed here get a handler; anything absent
 * shows "Coming soon" in the picker. Buckets group tools by measurement
 * geometry so the shape of the tool is obvious at review time.
 *
 * See handler files for what each shape draws + computes.
 */

const DISTANCE_TOOLS = [
  201, // Atlantodental Interspace
  303, // Thoracic Cage Dimension
  400, // Intervertebral Disc Height (Hurxthal)
  410, // Meyerding — approximated as a distance for now
  500, // Teardrop Distance
  504, // Symphysis Pubis Width
  505, // Presacral Space
  601, // Acromiohumeral Joint Space
  602, // Acromioclavicular Joint Space (approx)
];

const ANGLE_3_TOOLS = [
  102, // Basilar Angle
  108, // Boogard's Angle
];

const ANGLE_4_TOOLS = [
  202, // Method of Bull
  207, // Cervical Lordosis Angle
  300, // Cobb's Method
  302, // Thoracic Kyphosis
  403, // Lumbar Lordosis
  407, // Lumbosacral Disc Angle
  509, // Femoral Angle
  510, // Skinner's Line (approx: line-line angle)
];

const REFERENCE_LINE_TOOLS = [
  105, // MacRae's Line
  107, // Boogard's Line
  206, // Central Gravity Line
  411, // Ullmann's Line
  413, // Intercrestal Line
  508, // Protrusio
  702, // Horizontal Level
  703, // Vertical Level
];

const LINE_PERP_DIST_TOOLS = [
  103, // McGregor's Line
  104, // Chamberlain's Line
  106, // Height Index of Klaus
  208, // Cervical Lordosis Depth
  209, // Cervical Lordosis (Jochumsen)
  412, // Eisenstein — spinal canal AP dimension
  502, // Acetabular Depth
];

const ANGLE_FROM_HORIZONTAL_TOOLS = [
  405, // Sacral Inclination
  406, // Lumbosacral Angle
];

const TWO_LINES_INTERSECTION_TOOLS = [
  210, // Stress Lines Cervical Spine (Flexion)
  211, // Stress Lines Cervical Spine (Extension)
];

const RATIO_TOOLS = [
  109, // Anterior Atlanto-occipital Dislocation Measurement
  401, // Farfan Disc Height (posterior / anterior)
];

const tools = toolsData as ChiroTool[];
const nameOf = (id: number) => tools.find((t) => t.id === id)?.name ?? `Tool ${id}`;

const handlers: Record<number, ChiroToolHandler> = {};
for (const id of DISTANCE_TOOLS)              handlers[id] = distanceHandler(id, nameOf(id));
for (const id of ANGLE_3_TOOLS)               handlers[id] = angle3PointHandler(id, nameOf(id));
for (const id of ANGLE_4_TOOLS)               handlers[id] = angle4PointHandler(id, nameOf(id));
for (const id of REFERENCE_LINE_TOOLS)        handlers[id] = referenceLineHandler(id, nameOf(id));
for (const id of LINE_PERP_DIST_TOOLS)        handlers[id] = lineWithPerpDistanceHandler(id, nameOf(id));
for (const id of ANGLE_FROM_HORIZONTAL_TOOLS) handlers[id] = angleFromHorizontalHandler(id, nameOf(id));
for (const id of TWO_LINES_INTERSECTION_TOOLS) handlers[id] = twoLinesIntersectionHandler(id, nameOf(id));
for (const id of RATIO_TOOLS)                 handlers[id] = ratioHandler(id, nameOf(id));

// -- Fan (distances from a central landmark) --
handlers[100] = distancesFromCenterHandler(100, nameOf(100), 5); // Vastine-Kinney
handlers[101] = distancesFromCenterHandler(101, nameOf(101), 5); // Sella Turcica (approx)

// -- Per-pair distance measurements --
handlers[205] = perPairDistanceHandler(205, nameOf(205),
  ['C1', 'C2', 'C3', 'C4', 'C5', 'C6', 'C7']);
handlers[212] = perPairDistanceHandler(212, nameOf(212),
  ['C1', 'C2', 'C3', 'C4', 'C5', 'C6']);
handlers[501] = perPairDistanceHandler(501, nameOf(501),
  ['Superior', 'Axial', 'Medial']);

// -- Per-level angle (5 discs × 4 corners = 20 clicks) --
handlers[402] = perLevelAngleHandler(402, nameOf(402),
  ['L1-L2', 'L2-L3', 'L3-L4', 'L4-L5', 'L5-S1']);

// -- Polyline fallback: draws connected landmarks + total path length --
// These are the compound multi-landmark tools whose exact legacy math
// is elaborate. Polyline is a clinically useful placeholder — see the
// handler comment for what a bespoke replacement would compute.
const POLYLINE_TOOLS: [number, number][] = [
  [203, 14], // George's Line
  [204, 14], // Posterior Cervical Line
  [301, 12], // Risser-Ferguson
  [404, 12], // Lumbosacral Lordosis
  [408,  4], // Lumbar Gravity Line
  [409,  8], // Lateral Bending Sign
  [503,  2], // Center-Edge Angle (2 clicks - degenerates to a line)
  [506,  6], // Acetabular Angle
  [507,  6], // Iliac Angle and Index
  [511,  8], // Axial Relationships of the Knee
  [600,  6], // Glenohumeral Joint Space
  [700, 16], // Gonstead
  [701, 16], // Cervical Curve
];
for (const [id, n] of POLYLINE_TOOLS) handlers[id] = polylineHandler(id, nameOf(id), n);

export function getHandler(toolId: number): ChiroToolHandler | undefined {
  return handlers[toolId];
}

export function hasHandler(toolId: number): boolean {
  return toolId in handlers;
}
